import { useState } from 'react';
import { Asset, Edition, Stream } from '@24i/nxg-sdk-photon';
import { ISource, IPlayerError, IPlayerEngine } from '@24i/player-base';
import { useCastState, CastState } from '@24i/nxg-sdk-smartott/src/context/Cast';
import { useEffectWithCancelledFlag } from '@24i/nxg-sdk-smartott-shared/src/hooks/useEffectWithCancelledFlag';
import { usePlayerSourceManager } from '@24i/nxg-sdk-smartott-shared/src/hooks/usePlayerSourceManager';

export interface UseSourceResult {
    edition?: Edition;
    stream?: Stream;
    source?: ISource;
    sourceError?: Error | IPlayerError | null;
}

const useSourceEmptyResult: UseSourceResult = {
    source: undefined,
    sourceError: null,
};

export const useSource = (
    itemAsset?: Asset,
    engine?: IPlayerEngine,
    enabled = true
): UseSourceResult => {
    const [result, setResult] = useState<UseSourceResult>(useSourceEmptyResult);
    const isCastConnected = useCastState() === CastState.CONNECTED;
    const { getStream } = usePlayerSourceManager();

    useEffectWithCancelledFlag(
        async ({ getIsCancelled }) => {
            setResult(useSourceEmptyResult);
            if (!enabled || !itemAsset) return;
            try {
                const loadedStream = await getStream(itemAsset, {
                    isCastConnected,
                    engine,
                });
                if (getIsCancelled()) return;
                setResult({
                    ...loadedStream,
                    sourceError: null,
                });
            } catch (error: any) {
                if (getIsCancelled()) return;
                setResult({
                    sourceError: error,
                });
            }
        },
        [itemAsset, engine, isCastConnected, enabled]
    );

    return itemAsset ? result : useSourceEmptyResult;
};
